import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_TIME_ZONE, isValidTimeZone } from "../../shared/timezones";
import type { ChatInsights } from "../../shared/types";
import { LiveInbox } from "./LiveInbox";
import { KnowledgeBase } from "./KnowledgeBase";
import { GoogleLogin } from "./GoogleLogin";
import type { AdminSessionResponse } from "../../shared/admin-auth";
import { DEFAULT_AI_TAKEOVER_DELAY_SECONDS, MIN_AI_TAKEOVER_DELAY_SECONDS, MAX_AI_TAKEOVER_DELAY_SECONDS, resolveTakeoverDelay } from "../../shared/support-settings";

interface Site {
  connectionLocked: boolean;
  id: string;
  publicKey: string;
  name: string;
  storeUrl: string;
  allowedOrigins: string[];
  status: "active" | "paused";
  assistantName: string;
  greeting: string;
  systemPrompt: string;
  primaryColor: string;
  launcherPosition: "left" | "right";
  timeZone: string;
  aiTakeoverDelaySeconds: number;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  productCount: number;
}

interface ProductPreview {
  id: number;
  name: string;
  formattedPrice: string;
  stockStatus: string;
  permalink: string;
  images: Array<{ thumbnail: string; src: string; alt: string }>;
}

interface SyncProgress {
  runId: string;
  status: "running" | "completed" | "failed";
  productsSeen: number;
  productsWritten: number;
  productsTotal: number | null;
  pagesRead: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface FormState {
  name: string;
  storeUrl: string;
  allowedOrigins: string;
  assistantName: string;
  greeting: string;
  systemPrompt: string;
  primaryColor: string;
  launcherPosition: "left" | "right";
  timeZone: string;
  aiTakeoverDelaySeconds: string;
}

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const initialTimeZone = isValidTimeZone(browserTimeZone) ? browserTimeZone : DEFAULT_TIME_ZONE;
const timeZoneOptions = [
  initialTimeZone,
  "UTC",
  ...Intl.supportedValuesOf("timeZone"),
].filter((value, index, values) => values.indexOf(value) === index);

const initialForm: FormState = {
  name: "",
  storeUrl: "",
  allowedOrigins: "",
  assistantName: "Shop Assistant",
  greeting: "Hi! What can I help you find today?",
  systemPrompt: "",
  primaryColor: "#6d5dfc",
  launcherPosition: "right",
  timeZone: initialTimeZone,
  aiTakeoverDelaySeconds: String(DEFAULT_AI_TAKEOVER_DELAY_SECONDS),
};

const emptyChatInsights = (): ChatInsights => ({ interactions: [], recommendations: [] });

export function AdminApp() {
  const [session, setSession] = useState<AdminSessionResponse>({ user: null });
  const [authLoading, setAuthLoading] = useState(true);
  const [authProblem, setAuthProblem] = useState("");
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState<FormState>(initialForm);
  const [products, setProducts] = useState<ProductPreview[]>([]);
  const [webhook, setWebhook] = useState<{ deliveryUrl: string; secret: string; topics: string[] } | null>(null);
  const [chatInsights, setChatInsights] = useState<ChatInsights>(emptyChatInsights);
  const [chatSearchDraft, setChatSearchDraft] = useState("");
  const [chatQuery, setChatQuery] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatProblem, setChatProblem] = useState("");
  const [notice, setNotice] = useState("");
  const [problem, setProblem] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeSyncRunId, setActiveSyncRunId] = useState("");
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const selected = useMemo(() => sites.find((site) => site.id === selectedId) ?? null, [sites, selectedId]);

  const api = useCallback(async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const response = await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrfToken ?? "", ...init?.headers },
    });
    const body = await response.json() as T & { error?: string };
    if (response.status === 401) setSession({ user: null });
    if (!response.ok) throw new Error(body.error ?? `Request failed (${response.status})`);
    return body;
  }, [session.csrfToken]);

  useEffect(() => {
    sessionStorage.removeItem("woo-chat-admin-token");
    sessionStorage.removeItem("woo-chat-operator-id");
    let cancelled = false;
    void fetch("/api/auth/session", { credentials: "same-origin" }).then(async (response) => {
      if (!response.ok) throw new Error("Unable to check your login. Please refresh this page.");
      const value = await response.json() as AdminSessionResponse;
      if (!cancelled) setSession(value);
    }).catch((error: unknown) => { if (!cancelled) setAuthProblem(error instanceof Error ? error.message : "Unable to check login."); })
      .finally(() => { if (!cancelled) setAuthLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!session.user) {
      setSites([]); setSelectedId(""); setProducts([]); setWebhook(null); setChatInsights(emptyChatInsights()); setSyncing(false);
      return;
    }
    const timeout = window.setTimeout(() => setSession({ user: null }), Math.max(0, session.user.expiresAt * 1_000 - Date.now()));
    const channel = new BroadcastChannel("woo-admin-auth");
    channel.onmessage = (event) => { if (event.data === "logout") setSession({ user: null }); };
    return () => { window.clearTimeout(timeout); channel.close(); };
  }, [session.user]);

  const loadSites = useCallback(async () => {
    if (!session.user) return;
    try {
      const body = await api<{ sites: Site[] }>("/api/admin/sites");
      setSites(body.sites);
      setSelectedId((current) => body.sites.some((site) => site.id === current) ? current : body.sites[0]?.id || "");
      setProblem(body.sites.length ? "" : "The configured website is unavailable. Please contact the administrator.");
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Unable to load sites");
    }
  }, [api, session.user]);

  const loadChatInsights = useCallback(async (siteId: string, query = "") => {
    setChatLoading(true);
    setChatProblem("");
    try {
      const suffix = query ? `?q=${encodeURIComponent(query)}` : "";
      const body = await api<ChatInsights>(`/api/admin/sites/${encodeURIComponent(siteId)}/chat-insights${suffix}`);
      setChatInsights(body);
    } catch (error) {
      setChatProblem(error instanceof Error ? error.message : "Unable to load chat insights");
    } finally {
      setChatLoading(false);
    }
  }, [api]);

  useEffect(() => { void loadSites(); }, [loadSites]);
  useEffect(() => {
    let cancelled = false;
    setSyncProgress(null);
    setActiveSyncRunId("");
    setSyncing(false);
    if (!selectedId) return () => { cancelled = true; };

    void api<{ sync: SyncProgress | null }>(`/api/admin/sites/${selectedId}/sync-status`)
      .then(({ sync }) => {
        if (cancelled) return;
        setSyncProgress(sync);
        if (sync?.status === "running") {
          setActiveSyncRunId(sync.runId);
          setSyncing(true);
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [api, selectedId]);

  useEffect(() => {
    if (!syncing || !selectedId || !activeSyncRunId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const { sync } = await api<{ sync: SyncProgress | null }>(
          `/api/admin/sites/${selectedId}/sync-status?runId=${encodeURIComponent(activeSyncRunId)}`,
        );
        if (cancelled || !sync) return;
        setSyncProgress(sync);
        if (sync.status !== "running") {
          setSyncing(false);
          setActiveSyncRunId("");
          await loadSites();
        }
      } catch {
        // The foreground sync request reports actionable errors; polling can retry quietly.
      }
    };

    const initialTimer = window.setTimeout(() => void poll(), 350);
    const interval = window.setInterval(() => void poll(), 750);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
    };
  }, [activeSyncRunId, api, loadSites, selectedId, syncing]);

  useEffect(() => {
    if (!selected) return;
    setForm({
      name: selected.name,
      storeUrl: selected.storeUrl,
      allowedOrigins: selected.allowedOrigins.join("\n"),
      assistantName: selected.assistantName,
      greeting: selected.greeting,
      systemPrompt: selected.systemPrompt,
      primaryColor: selected.primaryColor,
      launcherPosition: selected.launcherPosition,
      timeZone: selected.timeZone,
      aiTakeoverDelaySeconds: String(resolveTakeoverDelay(selected.aiTakeoverDelaySeconds)),
    });
    setProducts([]);
    setWebhook(null);
    setChatSearchDraft("");
    setChatQuery("");
    setChatInsights(emptyChatInsights());
    void loadChatInsights(selected.id);
  }, [loadChatInsights, selected]);

  async function signOut() {
    try {
      await api("/api/auth/logout", { method: "POST" });
      const channel = new BroadcastChannel("woo-admin-auth");
      channel.postMessage("logout"); channel.close();
      setSession({ user: null });
    } catch (error) { setProblem(error instanceof Error ? error.message : "Unable to sign out. Please try again."); }
  }

  async function saveSite(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setProblem("");
    try {
      const payload = { ...form, allowedOrigins: lines(form.allowedOrigins), aiTakeoverDelaySeconds: Number(form.aiTakeoverDelaySeconds) };
      const path = `/api/admin/sites/${encodeURIComponent(selected.id)}`;
      const body = await api<{ site: Site }>(path, { method: "PATCH", body: JSON.stringify(payload) });
      setNotice("Site settings saved.");
      await loadSites();
      setSelectedId(body.site.id);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Unable to save the site");
    } finally {
      setSaving(false);
    }
  }

  async function syncSite() {
    if (!selected) return;
    const runId = crypto.randomUUID();
    setActiveSyncRunId(runId);
    setSyncProgress({
      runId,
      status: "running",
      productsSeen: 0,
      productsWritten: 0,
      productsTotal: null,
      pagesRead: 0,
      errorMessage: null,
      startedAt: new Date().toISOString(),
      finishedAt: null,
    });
    setSyncing(true);
    setProblem("");
    setNotice("");
    try {
      const body = await api<{ result: { productsSeen: number; productsWritten: number; pagesRead: number } }>(
        `/api/admin/sites/${selected.id}/sync?runId=${encodeURIComponent(runId)}`,
        { method: "POST" },
      );
      setSyncProgress((current) => ({
        runId,
        status: "completed",
        productsSeen: body.result.productsSeen,
        productsWritten: body.result.productsWritten,
        productsTotal: current?.productsTotal ?? body.result.productsSeen,
        pagesRead: body.result.pagesRead,
        errorMessage: null,
        startedAt: current?.startedAt ?? new Date().toISOString(),
        finishedAt: new Date().toISOString(),
      }));
      setNotice(`Sync complete: ${body.result.productsWritten} products updated across ${body.result.pagesRead} page${body.result.pagesRead === 1 ? "" : "s"}.`);
      await loadSites();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Catalogue sync failed");
      try {
        const { sync } = await api<{ sync: SyncProgress | null }>(
          `/api/admin/sites/${selected.id}/sync-status?runId=${encodeURIComponent(runId)}`,
        );
        if (sync) setSyncProgress(sync);
      } catch {
        // Keep the last visible count when the status request is unavailable.
      }
    } finally {
      setSyncing(false);
      setActiveSyncRunId("");
    }
  }

  async function loadProducts() {
    if (!selected) return;
    try {
      const body = await api<{ products: ProductPreview[] }>(`/api/admin/sites/${selected.id}/products?limit=24`);
      setProducts(body.products);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Unable to load products");
    }
  }

  async function loadWebhook() {
    if (!selected) return;
    try {
      const body = await api<{ deliveryUrl: string; secret: string; topics: string[] }>(`/api/admin/sites/${selected.id}/webhook`);
      setWebhook(body);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Unable to load webhook settings");
    }
  }

  function searchChats(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const query = chatSearchDraft.trim();
    setChatQuery(query);
    void loadChatInsights(selected.id, query);
  }

  async function toggleStatus() {
    if (!selected) return;
    try {
      await api(`/api/admin/sites/${selected.id}`, { method: "PATCH", body: JSON.stringify({ status: selected.status === "active" ? "paused" : "active" }) });
      await loadSites();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Unable to update status");
    }
  }

  if (authLoading || authProblem) return <main className="admin-login"><section className="login-card"><p role="status">{authProblem || "Checking your login…"}</p>{authProblem && <button className="button button-secondary" onClick={() => window.location.reload()}>Try again</button>}</section></main>;
  if (!session.user) return <GoogleLogin onSignIn={setSession} />;
  if (!selected) return <main className="admin-login"><section className="login-card"><h1>Shop assistant</h1><p role="status">{problem || "Loading your website…"}</p>{problem && <button className="button button-secondary" onClick={() => void loadSites()}>Try again</button>}<button className="text-button" onClick={() => void signOut()}>Sign out</button></section></main>;

  const snippet = selected ? `<script src="${window.location.origin}/widget.js" data-site="${selected.publicKey}" defer></script>` : "";
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <a className="admin-brand" href="/"><span>W</span><strong>Shop assistant</strong></a>
        <nav aria-label="Configured sites">
          {sites.map((site) => (
            <button className={site.id === selectedId ? "active" : ""} onClick={() => setSelectedId(site.id)} key={site.id}>
              <span style={{ background: site.primaryColor }}>{site.name.slice(0, 1).toUpperCase()}</span>
              <span><strong>{site.name}</strong><small>{site.productCount} products</small></span>
              <i className={site.status} />
            </button>
          ))}
        </nav>
        <a className="knowledge-nav" href="#knowledge-base">Knowledge base →</a>
        <div className="admin-account"><small>{session.user.email}</small><button className="sign-out" onClick={() => void signOut()}>Sign out</button></div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div><p className="eyebrow">Website configuration</p><h1>{selected.name}</h1></div>
          {selected && <div className="topbar-actions"><button className="button button-secondary" onClick={toggleStatus} disabled={syncing}>{selected.status === "active" ? "Pause" : "Activate"}</button><button className="button button-primary" onClick={syncSite} disabled={syncing || saving}>{syncing ? "Syncing…" : "Sync catalogue"}</button></div>}
        </header>

        {problem && <div className="alert alert-error">{problem}<button onClick={() => setProblem("")}>×</button></div>}
        {notice && <div className="alert alert-success">{notice}<button onClick={() => setNotice("")}>×</button></div>}

        {sites.length > 0 && <LiveInbox key={session.user.operatorId} user={session.user} sites={sites} selectedSiteId={selectedId} onSelectSite={setSelectedId} api={api} />}

        <KnowledgeBase key={selected.id} siteId={selected.id} api={api} />

        <div className="admin-grid">
          <form className="panel settings-panel" onSubmit={saveSite}>
            <div className="panel-heading"><div><h2>Store connection</h2><p>Public catalogue data is read from the WooCommerce Store API.</p></div>{selected && <span className={`status-pill ${selected.status}`}>{selected.status}</span>}</div>
            <div className="field-grid">
              <label>Display name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Example Outfitters" required /></label>
              <label>WooCommerce URL<input type="url" value={form.storeUrl} readOnly={selected.connectionLocked} onChange={(event) => setForm({ ...form, storeUrl: event.target.value })} required /></label>
              <label className="full-field">Allowed website origins<textarea rows={3} value={form.allowedOrigins} readOnly={selected.connectionLocked} onChange={(event) => setForm({ ...form, allowedOrigins: event.target.value })} required /><small>{selected.connectionLocked ? "This assistant is restricted to this website. The store connection cannot be changed here." : "One exact origin per line. Wildcards are intentionally discouraged."}</small></label>
            </div>

            <div className="section-rule" />
            <div className="panel-heading"><div><h2>Assistant personality</h2><p>Answers use synced products and your published knowledge base.</p></div></div>
            <div className="field-grid">
              <label>Assistant name<input value={form.assistantName} onChange={(event) => setForm({ ...form, assistantName: event.target.value })} required /></label>
              <label>Brand colour<span className="color-field"><input type="color" value={form.primaryColor} onChange={(event) => setForm({ ...form, primaryColor: event.target.value })} /><input value={form.primaryColor} onChange={(event) => setForm({ ...form, primaryColor: event.target.value })} pattern="#[0-9a-fA-F]{6}" /></span></label>
              <label className="full-field">Welcome message<textarea rows={2} value={form.greeting} onChange={(event) => setForm({ ...form, greeting: event.target.value })} required /></label>
              <label className="full-field">Store-specific guidance<textarea rows={4} value={form.systemPrompt} onChange={(event) => setForm({ ...form, systemPrompt: event.target.value })} placeholder="Tone, audience, sizing guidance, or product priorities…" /></label>
              <label>Launcher position<select value={form.launcherPosition} onChange={(event) => setForm({ ...form, launcherPosition: event.target.value as "left" | "right" })}><option value="right">Bottom right</option><option value="left">Bottom left</option></select></label>
              <label className="full-field">Timezone<select value={form.timeZone} onChange={(event) => setForm({ ...form, timeZone: event.target.value })}>{timeZoneOptions.map((timeZone) => <option value={timeZone} key={timeZone}>{timeZoneLabel(timeZone)}</option>)}</select><small>Used for dates and times shown in this admin.</small></label>
              <label className="full-field">AI takeover delay (seconds)<input type="number" min={MIN_AI_TAKEOVER_DELAY_SECONDS} max={MAX_AI_TAKEOVER_DELAY_SECONDS} step="1" required value={form.aiTakeoverDelaySeconds} onChange={(event) => setForm({ ...form, aiTakeoverDelaySeconds: event.target.value })} /><small>How long online admins have to reply before AI steps in. Choose 10–600 seconds (up to 10 minutes). Applies to new waits after saving; existing countdowns stay unchanged.</small></label>
            </div>
            <div className="form-actions"><button type="submit" className="button button-primary" disabled={saving || syncing}>{saving ? "Saving…" : "Save changes"}</button></div>
          </form>

          <aside className="admin-side-stack">
            <section className="panel metric-panel">
              <div className="metric-title"><p>Catalogue</p>{syncing && <span className="live-badge"><i /> Live sync</span>}</div>
              <strong aria-live="polite">{formatCount(syncing ? syncProgress?.productsSeen ?? 0 : selected?.productCount ?? 0)}</strong>
              <span>{syncing ? syncCountLabel(syncProgress) : "synced products"}</span>
              {syncing && <SyncProgressBar progress={syncProgress} />}
              {syncing
                ? <small className="sync-status">Scanning page {Math.max((syncProgress?.pagesRead ?? 0) + 1, 1)}…</small>
                : selected?.lastSyncedAt && <small>Last sync {formatDate(selected.lastSyncedAt, selected.timeZone)}</small>}
              {selected && <button className="text-button" onClick={loadProducts} disabled={syncing}>Preview catalogue →</button>}
            </section>

            {selected && <section className="panel install-panel">
              <h2>Install on your site</h2><p>Add this once before the closing <code>&lt;/body&gt;</code> tag.</p>
              <pre><code>{snippet}</code></pre>
              <button className="button button-secondary" onClick={() => void navigator.clipboard.writeText(snippet).then(() => setNotice("Embed code copied."))}>Copy embed code</button>
            </section>}

            {selected && <section className="panel webhook-panel">
              <h2>Live product updates</h2><p>Create three WooCommerce webhooks so catalogue changes trigger a fresh sync.</p>
              {!webhook ? <button className="text-button" onClick={loadWebhook}>Show webhook settings →</button> : <WebhookDetails webhook={webhook} />}
            </section>}

            {selected && <ChatInsightsPanel
              assistantName={selected.assistantName}
              timeZone={selected.timeZone}
              insights={chatInsights}
              loading={chatLoading}
              problem={chatProblem}
              searchDraft={chatSearchDraft}
              activeQuery={chatQuery}
              onSearchDraftChange={setChatSearchDraft}
              onSearch={searchChats}
              onRefresh={() => void loadChatInsights(selected.id, chatQuery)}
            />}
          </aside>
        </div>

        {products.length > 0 && <section className="panel products-panel"><div className="panel-heading"><div><h2>Catalogue preview</h2><p>First {products.length} products available to Luna.</p></div><button className="icon-button" onClick={() => setProducts([])}>×</button></div><div className="admin-products">{products.map((product) => <a href={product.permalink} target="_blank" rel="noreferrer" key={product.id}>{product.images[0] ? <img src={product.images[0].thumbnail || product.images[0].src} alt={product.images[0].alt || product.name} /> : <span /> }<strong>{product.name}</strong><small>{product.formattedPrice}</small></a>)}</div></section>}
      </main>
    </div>
  );
}

function ChatInsightsPanel({
  assistantName,
  timeZone,
  insights,
  loading,
  problem,
  searchDraft,
  activeQuery,
  onSearchDraftChange,
  onSearch,
  onRefresh,
}: {
  assistantName: string;
  timeZone: string;
  insights: ChatInsights;
  loading: boolean;
  problem: string;
  searchDraft: string;
  activeQuery: string;
  onSearchDraftChange: (value: string) => void;
  onSearch: (event: FormEvent) => void;
  onRefresh: () => void;
}) {
  return (
    <section className="panel chat-insights-panel">
      <div className="chat-insights-heading">
        <div><h2>Chat insights</h2><p>Recent chats are retained for 60 days.</p></div>
        <button className="icon-button" type="button" onClick={onRefresh} aria-label="Refresh chat insights" title="Refresh">↻</button>
      </div>
      <form className="chat-search" onSubmit={onSearch}>
        <label className="sr-only" htmlFor="chat-search-input">Search recent chats</label>
        <input id="chat-search-input" type="search" value={searchDraft} onChange={(event) => onSearchDraftChange(event.target.value)} placeholder="Search recent chats" maxLength={120} />
        <button type="submit" disabled={loading}>Search</button>
      </form>
      {problem && <p className="chat-insights-error">{problem}</p>}

      <div className="insight-section">
        <div className="insight-section-title"><h3>Chat engagement</h3><span>30 days · UTC</span></div>
        <dl className="engagement-metrics">
          {([
            ["opened", "Opened chat"], ["started", "Sent a message"],
            ["product_clicked", "Clicked a product"], ["product_link", "Used product help link"],
            ["hint_shown", "Saw a hint"], ["hint_opened", "Opened from hint"], ["hint_dismissed", "Dismissed hint"],
          ] as const).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{formatCount(insights.engagement?.[key] ?? 0)}</dd></div>)}
        </dl>
        <p>Distinct conversations per action, not raw clicks. Tracking starts with this update and may be blocked by browser privacy settings. These counts measure interest, not purchases.</p>
      </div>

      <div className="insight-section">
        <div className="insight-section-title"><h3>Recent interactions</h3><span>{insights.interactions.length}</span></div>
        {loading && insights.interactions.length === 0
          ? <p className="insight-empty">Loading chats…</p>
          : insights.interactions.length === 0
            ? <p className="insight-empty">{activeQuery ? "No chats match this search." : "New chat interactions will appear here."}</p>
            : <div className="chat-interaction-list">
              {insights.interactions.map((interaction) => (
                <details className="chat-interaction" key={interaction.id}>
                  <summary>
                    <span>{interaction.userText || "Customer interaction"}</span>
                    <time dateTime={interaction.createdAt}>{formatDate(interaction.createdAt, timeZone)}</time>
                  </summary>
                  <div className="chat-interaction-body">
                    {interaction.status !== "completed" && <span className={`interaction-status ${interaction.status}`}>{interaction.status}</span>}
                    <p><strong>Customer</strong>{interaction.userText || "No customer text was recorded."}</p>
                    <p><strong>{assistantName}</strong>{interaction.assistantText || "No assistant text was recorded."}</p>
                    {interaction.products.length > 0 && <div className="interaction-products">
                      {interaction.products.map((product) => <a href={product.permalink} target="_blank" rel="noreferrer" key={product.id}>{product.name}</a>)}
                    </div>}
                  </div>
                </details>
              ))}
            </div>}
      </div>

      <div className="insight-section recommendation-insights">
        <div className="insight-section-title"><h3>Products recommended</h3><span>Lifetime</span></div>
        <p>Totals are kept permanently from the day tracking began, even after chats are removed.</p>
        {insights.recommendations.length === 0
          ? <p className="insight-empty">Product recommendations will be tallied here.</p>
          : <ol className="recommendation-list">
            {insights.recommendations.map((product) => (
              <li key={product.productId}>
                <span>
                  <a href={product.permalink} target="_blank" rel="noreferrer">{product.productName}</a>
                  <small>Last recommended {formatDate(product.lastRecommendedAt, timeZone)}</small>
                </span>
                <strong>{formatCount(product.recommendationCount)}</strong>
              </li>
            ))}
          </ol>}
      </div>
    </section>
  );
}

function SyncProgressBar({ progress }: { progress: SyncProgress | null }) {
  const percent = progress?.productsTotal
    ? Math.min(100, Math.max(0, progress.productsSeen / progress.productsTotal * 100))
    : null;
  return (
    <div
      className={`sync-progress ${percent === null ? "indeterminate" : ""}`}
      role="progressbar"
      aria-label="Catalogue sync progress"
      aria-valuemin={0}
      aria-valuemax={progress?.productsTotal ?? undefined}
      aria-valuenow={progress?.productsSeen ?? 0}
    >
      <span style={percent === null ? undefined : { width: `${percent}%` }} />
    </div>
  );
}

function syncCountLabel(progress: SyncProgress | null): string {
  if (progress?.productsTotal) return `of ${formatCount(progress.productsTotal)} products found`;
  return "products found so far";
}

function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function WebhookDetails({ webhook }: { webhook: { deliveryUrl: string; secret: string; topics: string[] } }) {
  return <dl className="webhook-values"><dt>Delivery URL</dt><dd>{webhook.deliveryUrl}</dd><dt>Secret</dt><dd>{webhook.secret}</dd><dt>Topics</dt><dd>{webhook.topics.join(", ")}</dd></dl>;
}

function lines(value: string): string[] {
  return value.split(/[\n,]/).map((line) => line.trim()).filter(Boolean);
}

function timeZoneLabel(value: string): string {
  return value === "UTC" ? "UTC" : value.replaceAll("_", " ");
}

function formatDate(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(value));
}
